import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';

/** The Team section of Settings, where the team is invited. */
const TEAM_SETTINGS_PATH = `${RouterPaths.settings}?section=${SettingsTabs.TEAM}`;

/**
 * The aside's invitation to bring the team in, once the number is connected.
 * Its link closes the wizard and opens the Team section of Settings inside the
 * app. Opened from onboarding, it only closes the wizard with that section as
 * its exit destination: the onboarding step marks onboarding complete first,
 * since leaving the step before that sends the owner back to it. Settings hides that section from a seller, so a seller sees neither the
 * line nor the link.
 *
 * @returns {JSX.Element | null} The invitation, or null for a seller.
 */
export default function WhatsAppConnectionWizardTeamInvite() {
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const { currentUserRole } = useUserContext();
  const {
    whatsAppBusinessConnectionModalSource,
    setWhatsAppBusinessConnectionModalIsOpen,
  } = useModalsContext();

  if (currentUserRole === eUserRole.SELLER) return null;

  function openTeamSection(event: MouseEvent<HTMLAnchorElement>) {
    if (whatsAppBusinessConnectionModalSource === 'onboarding') {
      event.preventDefault();
      setWhatsAppBusinessConnectionModalIsOpen(false, {
        exitDestination: TEAM_SETTINGS_PATH,
      });
      return;
    }
    setWhatsAppBusinessConnectionModalIsOpen(false);
  }

  return (
    <div className="flex flex-col items-start gap-[var(--ls-spacing-2)]">
      <p className={WIZARD_BODY_CLASS_NAME}>
        {t('wizard.asideConnection.inviteTeam')}
      </p>
      {/* The DS Link is a plain anchor, which would reload the app; the
          router's Link navigates in place with the DS link look. */}
      <Link
        to={TEAM_SETTINGS_PATH}
        className="ls-link"
        onClick={openTeamSection}
      >
        {t('wizard.asideConnection.inviteTeamLink')}
      </Link>
    </div>
  );
}
